import { useState } from 'react';

import type { Campaign } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';

/**
 * Return type for the useCreativeRequirementsCard hook.
 */
export interface IUseCreativeRequirementsCardResult {
  /** Whether the card should render (at least one channel is active). */
  isVisible: boolean;
  /** Whether the campaign uses the display (Tradedesk) channel. */
  usesDisplayChannel: boolean;
  /** Whether the campaign uses the Meta (Facebook) channel. */
  usesMetaChannel: boolean;
  /** Whether the display "Show more" section is expanded. */
  displayExpanded: boolean;
  /** Whether the Meta "Show more" section is expanded. */
  metaExpanded: boolean;
  /** Expands all sections if any are collapsed, collapses all if all are expanded. */
  handleViewAll: () => void;
  /** Toggles the display "Show more" section. */
  handleToggleDisplay: () => void;
  /** Toggles the Meta "Show more" section. */
  handleToggleMeta: () => void;
}

/**
 * Custom hook that manages state and derived values for CreativeRequirementsCard.
 *
 * Handles:
 * - Detecting which channels (display / Meta) the campaign uses
 * - Expand/collapse state for each section's "Show more"
 * - "View all" toggle that expands or collapses all sections at once
 *
 * @param campaign - The campaign model to read integrations from
 * @returns State, handlers, and derived booleans for the card
 *
 * @example
 * const { isVisible, usesDisplayChannel, handleViewAll } = useCreativeRequirementsCard(campaign);
 */
export function useCreativeRequirementsCard(
  campaign: Campaign,
): IUseCreativeRequirementsCardResult {
  const [displayExpanded, setDisplayExpanded] = useState(false);
  const [metaExpanded, setMetaExpanded] = useState(false);

  const integrations = campaign.get('integrations');
  const usesDisplayChannel = integrations?.includes(EIntegrationTypes.Tradedesk) ?? false;
  const usesMetaChannel = integrations?.includes(EIntegrationTypes.Meta) ?? false;
  const isVisible = usesDisplayChannel || usesMetaChannel;

  function handleViewAll(): void {
    const expand = !displayExpanded || !metaExpanded;
    setDisplayExpanded(expand);
    setMetaExpanded(expand);
  }

  function handleToggleDisplay(): void {
    setDisplayExpanded((prev) => !prev);
  }

  function handleToggleMeta(): void {
    setMetaExpanded((prev) => !prev);
  }

  return {
    isVisible,
    usesDisplayChannel,
    usesMetaChannel,
    displayExpanded,
    metaExpanded,
    handleViewAll,
    handleToggleDisplay,
    handleToggleMeta,
  };
}
